import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { fetchStockBacktest } from "../lib/api";

export default function StockDetailModal({ symbol, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!symbol) return;
    setLoading(true);
    setErr(null);
    fetchStockBacktest(symbol)
      .then((d) => setData(d))
      .catch((e) => setErr(e?.message || "Failed to load"))
      .finally(() => setLoading(false));
  }, [symbol]);

  return (
    <AnimatePresence>
      {symbol && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose}
          data-testid="stock-detail-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
            className="glass rounded-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col"
          >
            <div className="flex items-center justify-between p-5 border-b border-white/8">
              <div className="flex items-center gap-3">
                <span className="ticker-badge text-sm">{symbol}</span>
                <span className="text-white/60 text-sm">{data?.name}</span>
              </div>
              <button data-testid="stock-detail-close" onClick={onClose} className="p-2 rounded-full hover:bg-white/8 text-white/70">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto">
              {loading && <div className="text-sm text-white/50">Loading backtest…</div>}
              {err && <div className="text-sm text-[#FF3B30]">{err}</div>}
              {data && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                    <Metric label="Signals" value={data.metrics.total_signals} />
                    <Metric label="Win rate" value={`${data.metrics.win_rate}%`} tone={data.metrics.win_rate >= 50 ? "green" : "red"} />
                    <Metric label="Avg P&L" value={`${data.metrics.avg_pnl_pct >= 0 ? "+" : ""}${data.metrics.avg_pnl_pct}%`} tone={data.metrics.avg_pnl_pct >= 0 ? "green" : "red"} />
                    <Metric label="Max DD" value={`${data.metrics.max_drawdown_pct}%`} tone="red" />
                    <Metric label="Best" value={`+${data.metrics.best_trade_pct}%`} tone="green" />
                    <Metric label="Worst" value={`${data.metrics.worst_trade_pct}%`} tone="red" />
                    <Metric label="Avg win" value={`+${data.metrics.avg_win_pct}%`} tone="green" />
                    <Metric label="Avg loss" value={`${data.metrics.avg_loss_pct}%`} tone="red" />
                  </div>

                  <div className="card-surface rounded-xl overflow-hidden">
                    <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-white/8 text-[10px] font-mono tracking-widest uppercase text-white/45">
                      <div className="col-span-3">Signal (IST)</div>
                      <div className="col-span-1 text-right">Lvl</div>
                      <div className="col-span-2 text-right">Entry</div>
                      <div className="col-span-2 text-right">SL</div>
                      <div className="col-span-2 text-right">Exit</div>
                      <div className="col-span-2 text-right">P&L</div>
                    </div>
                    <div className="max-h-[380px] overflow-y-auto">
                      {(data.signals || []).slice().reverse().map((s, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 px-4 py-2.5 border-b border-white/5 last:border-b-0 items-center">
                          <div className="col-span-3 font-mono text-xs text-white/70">{new Date(s.signal_time).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false })}</div>
                          <div className="col-span-1 text-right font-mono text-xs text-[#00E5FF]">{s.level}</div>
                          <div className="col-span-2 text-right font-mono text-xs text-white">₹{s.entry}</div>
                          <div className="col-span-2 text-right font-mono text-xs text-white/50">₹{s.stop_loss}</div>
                          <div className="col-span-2 text-right font-mono text-xs text-white">₹{s.exit}</div>
                          <div className={`col-span-2 text-right font-mono text-xs ${s.pnl_pct >= 0 ? "text-[#39FF14]" : "text-[#FF3B30]"}`}>
                            {s.pnl_pct >= 0 ? "+" : ""}{s.pnl_pct}% <span className="text-white/35 ml-1">{s.exit_reason === "stop_loss" ? "(SL)" : "(T)"}</span>
                          </div>
                        </div>
                      ))}
                      {(!data.signals || data.signals.length === 0) && (
                        <div className="py-10 text-center text-sm text-white/40">No historical signals for this stock.</div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Metric({ label, value, tone }) {
  const cls = tone === "green" ? "text-[#39FF14]" : tone === "red" ? "text-[#FF3B30]" : "text-white";
  return (
    <div className="card-surface rounded-xl p-3">
      <div className="text-[10px] font-mono tracking-widest text-white/45 uppercase">{label}</div>
      <div className={`mt-1 font-display text-xl font-semibold ${cls}`}>{value}</div>
    </div>
  );
}
